/** Pieces shared by several screens: the headline block, segmented control, loading and empty states. */

import type { ReactNode } from 'react';
import type { Snapshot } from '../api/types.js';
import { usePanel } from '../app/context.js';
import { Link, navigate } from '../app/router.js';
import { counts, type Inbox, inbox, inboxParts, oldestApprovalAge, reviewStart } from '../app/selectors.js';
import { Icon, LogoTile } from '../components/icons.js';
import { TierBadge } from '../components/primitives.js';
import { formatAge } from '../lib/format.js';

/** Digits of the "needs Sercan" count are accent; the rest of the sentence is ink. */
export function Accent({ children }: { children: ReactNode }) {
  return <span className="accent">{children}</span>;
}

export function SummaryHeadline({
  title,
  subline,
  tall = false,
  parts = null,
  action = null,
}: {
  title: ReactNode;
  subline: ReactNode;
  tall?: boolean;
  /** The inbox breakdown line under the title (Bugün). */
  parts?: ReactNode;
  /** A button under the lines (Bugün's "Sırayla incele"). */
  action?: ReactNode;
}) {
  const grows = parts !== null || action !== null;
  const className = ['headline', tall ? 'headline--tall' : null, grows ? 'headline--inbox' : null, 'drafting']
    .filter((name) => name !== null)
    .join(' ');
  return (
    <section className={className}>
      <h1 className="headline__title" aria-live="polite">
        {title}
      </h1>
      {parts === null ? null : <p className="headline__parts">{parts}</p>}
      <p className="headline__sub">{subline}</p>
      {action}
    </section>
  );
}

/** "3 onay · 4 istek · 2 gönderilecek · Sekreter'de 1 madde", each part a link to its page. */
export function InboxParts({ box }: { box: Inbox }) {
  const parts = inboxParts(box);
  return (
    <>
      {parts.map((part, index) => (
        <span key={part.key} className="headline__part">
          {index > 0 ? <span aria-hidden="true"> · </span> : null}
          <Link to={part.to}>{part.text}</Link>
        </span>
      ))}
    </>
  );
}

/** "Sırayla incele": the first approval, then whatever else waits; it opens, it never approves. */
export function ReviewButton({ snapshot, box }: { snapshot: Snapshot; box: Inbox }) {
  const start = reviewStart(snapshot);
  if (start === null) return null;
  const first = inboxParts(box)[0];
  return (
    <button type="button" className="review-btn headline__review" onClick={() => navigate(start)}>
      Sırayla incele
      <span className="review-btn__sep" aria-hidden="true" />
      <span className="mono review-btn__count">{box.approvals > 0 ? `${box.approvals} onay` : first?.text}</span>
      <Icon name="chevron-right" size={18} />
    </button>
  );
}

/** The inbox headline's title: one number, or the one kind of thing that waits in its own words. */
export function inboxTitle(box: Inbox): ReactNode {
  const kinds = inboxParts(box);
  if (kinds.length > 1) {
    return (
      <>
        <Accent>{box.total}</Accent> iş seni bekliyor
      </>
    );
  }
  if (box.approvals > 0) {
    return (
      <>
        <Accent>{box.approvals}</Accent> onay seni bekliyor
      </>
    );
  }
  if (box.requests > 0) {
    return (
      <>
        <Accent>{box.requests}</Accent> istek seni bekliyor
      </>
    );
  }
  if (box.outbox > 0) {
    return (
      <>
        <Accent>{box.outbox}</Accent> mesaj elle gönderilmeyi bekliyor
      </>
    );
  }
  return (
    <>
      Sekreter'de <Accent>{box.sekreter}</Accent> madde seni bekliyor
    </>
  );
}

/** "Denetçi 10 koşudur düşüyor" from the second failure on, "Denetçi duruyor" for a single one (v0.1.51). */
export function stoppedLabel(stopped: Snapshot['attention']['stoppedAgents'][number]): string {
  return stopped.streak >= 2 ? `${stopped.name} ${stopped.streak} koşudur düşüyor` : `${stopped.name} duruyor`;
}

/**
 * The inbox headline (audit 25 Oct 2026, F14): one number for everything that waits for Sercan (approvals,
 * requests, messages to send, Sekreter items) with the breakdown under it and, on Bugün, "Sırayla incele".
 * With an empty inbox it still says what waits elsewhere (v0.1.39): unanswered customer mails or a stopped
 * agent; only when nothing waits anywhere does it say so. A failing agent is the headline even while the
 * inbox is full (v0.1.51); the inbox moves to the subline.
 */
export function InboxHeadline({
  snapshot,
  now,
  review = false,
}: {
  snapshot: Snapshot;
  now: number;
  review?: boolean;
}) {
  const box = inbox(snapshot);
  const attention = snapshot.attention;
  const stopped = attention.stoppedAgents[0] ?? null;
  const action = review ? <ReviewButton snapshot={snapshot} box={box} /> : null;
  // Decisions and sends come first; with none of them, a stuck customer mail or a stopped agent outranks
  // Sekreter items, as before the one-number inbox.
  const decisions = box.approvals + box.requests + box.outbox;
  if (decisions === 0 && attention.unansweredMails > 0) {
    return (
      <SummaryHeadline
        title={
          <>
            <Accent>{attention.unansweredMails}</Accent> müşteri e-postası cevap bekliyor
          </>
        }
        subline={
          stopped !== null ? (
            <>
              {stoppedLabel(stopped)}: {stopped.reason} · <Link to="/ekip">Ekip</Link>
            </>
          ) : attention.failedMails > 0 ? (
            <>
              Son denemeler hata aldı · <Link to="/ekip">Ekip</Link>
            </>
          ) : (
            'Temsilci taslakları hazırlıyor; hazır olanlar Onaylar’a düşer'
          )
        }
        action={action}
      />
    );
  }
  if (stopped !== null) {
    const waiting = inboxParts(box).map((part) => part.text);
    return (
      <SummaryHeadline
        title={stoppedLabel(stopped)}
        subline={
          <>
            {stopped.reason} · <Link to="/ekip">Ekip</Link>
            {waiting.length > 0 ? <> · {waiting.join(', ')} bekliyor</> : null}
          </>
        }
        action={action}
      />
    );
  }
  if (box.total === 0) {
    return (
      <SummaryHeadline
        title="Seni bekleyen bir şey yok"
        subline="Ekip çalışıyor. Bir şey gelince Telegram'dan haber veririz."
      />
    );
  }
  const several = inboxParts(box).length > 1;
  const mails = attention.unansweredMails > 0 ? `${attention.unansweredMails} e-posta cevap bekliyor` : null;
  return (
    <SummaryHeadline
      title={inboxTitle(box)}
      parts={several ? <InboxParts box={box} /> : null}
      subline={
        box.approvals > 0 ? (
          <>
            <TierSubline snapshot={snapshot} now={now} />
            {mails === null ? null : <span> · {mails}</span>}
          </>
        ) : box.sekreter > 0 && !several ? (
          <Link to="/sekreter">Diğer › Sekreter</Link>
        ) : (
          (mails ?? 'Sırası gelince Telegram haber verir.')
        )
      }
      action={action}
    />
  );
}

/** "2 × [T3 DIŞ] · 1 × [T4 PARA] · en eskisi 9 dk" */
export function TierSubline({ snapshot, now }: { snapshot: Snapshot; now: number }) {
  const c = counts(snapshot);
  const age = oldestApprovalAge(snapshot, now);
  return (
    <>
      {c.t3 > 0 ? (
        <>
          {c.t3} × <TierBadge tier="T3" topic="DIŞ" variant="mini" />
        </>
      ) : null}
      {c.t3 > 0 && c.t4 > 0 ? <span aria-hidden="true">·</span> : null}
      {c.t4 > 0 ? (
        <>
          {c.t4} × <TierBadge tier="T4" topic={c.t4Topics[0] === 'ÜRÜN' ? 'ÜRÜN' : 'PARA'} variant="mini" />
        </>
      ) : null}
      {age !== null ? (
        <>
          <span aria-hidden="true">·</span> en eskisi {formatAge(age)}
        </>
      ) : null}
    </>
  );
}

function segmentClass(active: boolean, separated: boolean): string {
  if (active) return 'segmented__tab segmented__tab--on';
  return separated ? 'segmented__tab segmented__tab--sep' : 'segmented__tab';
}

export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  controls,
}: {
  label: string;
  options: readonly { id: T; label: string; count: number | null }[];
  value: T;
  onChange: (id: T) => void;
  controls: string;
}) {
  return (
    <div className="segmented" role="tablist" aria-label={label}>
      {options.map((option, index) => (
        <button
          key={option.id}
          type="button"
          role="tab"
          id={`tab-${controls}-${option.id}`}
          aria-selected={option.id === value}
          aria-controls={controls}
          className={segmentClass(option.id === value, index > 0 && options[index - 1]?.id !== value)}
          onClick={() => onChange(option.id)}
        >
          {option.label}
          {option.count !== null ? <span className="mono segmented__count">{option.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function EmptyState({ title, text }: { title: string; text: string }) {
  return (
    <div className="empty drafting">
      <span className="empty__logo">
        <LogoTile size={48} />
      </span>
      <p className="empty__title">{title}</p>
      <p className="empty__text">{text}</p>
    </div>
  );
}

export function Loading() {
  const state = usePanel();
  return (
    <div className="loading" role="status">
      {state.loadError ? `Veri yüklenemedi: ${state.loadError}` : 'Yükleniyor…'}
    </div>
  );
}
